/**
 * 下单页 / 会员专区：金属列表、编码规范与盘价映射
 * <p>金属编码规范：首字母大写（Au / Ag / Pt / Pd、Au999、Au22K…）；匹配时忽略大小写。</p>
 * <p>盘价 Symbol（如 JZJ_au、BR_Ag999）保持行情源原样，不改写。</p>
 * <p>OrderPrice 提交字段（au / au999…）保持后端 Java 字段小写，仅作 API 映射。</p>
 */

/** 付费会员基础金属大类 */
export const PAID_BASE_METALS = ['Au', 'Ag', 'Pt', 'Pd']

/** 付费会员类型：01银卡月 02金卡月 03银卡季 04金卡季（与后端 MemberTypeEnum 一致） */
export const PAID_MEMBER_TYPES = new Set(['01', '02', '03', '04'])

/**
 * 是否生效中的付费会员。
 * <p>仅认新体系 01~04 且 status=1。旧体系普通会员存的是 1（或 0），不能当付费会员。</p>
 */
export function isActivePaidMember(memberInfo) {
  if (!memberInfo) return false
  const type = String(memberInfo.memberType || memberInfo.type || '00').trim()
  const status = Number(memberInfo.status)
  return PAID_MEMBER_TYPES.has(type) && status === 1
}

/**
 * 是否付费会员类型（只看编码，不看是否过期）。
 * <p>旧值 1/0 视为普通会员；订单快照 memberType 用于检测品类体系。</p>
 */
export function isPaidMemberType(memberType) {
  const type = String(memberType ?? '00').trim()
  return PAID_MEMBER_TYPES.has(type)
}

/**
 * 解析金属展示名：会员用 memberName，非会员用 name（两者一一对应）。
 * @param {Object} item Metal_Order 项
 * @param {boolean} [forMember=false]
 */
export function resolveMetalDisplayName(item, forMember = false) {
  if (!item) return ''
  const original = String(item.originalName || item.name || item.label || '').trim()
  const member = String(item.memberName || original).trim()
  return forMember ? member : original
}

/**
 * 下单金属列表：全量；forMember 时展示名切为 memberName。
 * @param {Array} allTypes
 * @param {boolean} [forMember=false]
 */
export function filterOrderMetalTypes(allTypes, forMember = false) {
  if (!Array.isArray(allTypes)) return []
  return allTypes.map((item) => {
    const originalName = String(item.originalName || item.name || '').trim()
    const memberName = String(item.memberName || originalName).trim()
    const name = forMember ? memberName : originalName
    return { ...item, originalName, memberName, name, label: name, value: name }
  })
}

/**
 * 按 metalCode 解析展示名（体验券切换 / 精检匹配用）。
 * @param {string} metalCode
 * @param {Array} metalList
 * @param {boolean} [forMember=false]
 */
export function resolveMetalNameByCode(metalCode, metalList, forMember = false) {
  if (!metalCode || !Array.isArray(metalList)) return ''
  const code = normalizeMetalCode(metalCode)
  const compact = compactMetalCode(metalCode)
  const hit = metalList.find((m) => {
    const mc = normalizeMetalCode(m.metalCode)
    return mc === code || compactMetalCode(m.metalCode) === compact
  })
  return hit ? resolveMetalDisplayName(hit, forMember) : ''
}

/**
 * 规范化金属编码：基础符为 Au/Ag/Pt/Pd，其后后缀原样保留（如 Au999、Au_18K）。
 * 与后端 OrderFeeUtils.normalizeMetalCode 对齐。
 */
export function normalizeMetalCode(metalNum) {
  const s = String(metalNum || '').trim()
  if (!s) return ''
  if (s.length < 2) return s
  const base = s.charAt(0).toUpperCase() + s.charAt(1).toLowerCase()
  if (PAID_BASE_METALS.includes(base)) {
    return base + s.substring(2)
  }
  return s
}

/**
 * 金属编码前两位大类前缀（Au/Ag/Pt/Pd），用于右模糊匹配（Au → Au%、Au999、Au22K）。
 */
export function metalCodePrefix(metalNum) {
  return resolveMetalBase(metalNum)
}

/**
 * 前两位右模糊：candidate 是否以 pattern 的大类前缀开头（忽略大小写）。
 * 例：pattern=Au/Au999，candidate=Au18K → true
 */
export function matchesMetalPrefix(pattern, candidate) {
  const prefix = metalCodePrefix(pattern)
  if (!prefix || !candidate) return false
  const normalized = normalizeMetalCode(candidate)
  if (normalized.length < 2) return false
  return normalized.toLowerCase().startsWith(prefix.toLowerCase())
}

/** 紧凑金属编码：去掉下划线，便于 Au_18K 与 Au18K / au18k 互认 */
export function compactMetalCode(metalNum) {
  const normalized = normalizeMetalCode(metalNum)
  return normalized ? normalized.replace(/_/g, '') : ''
}

/**
 * 解析金属基础编码（Au / Ag / Pt / Pd）——取前两位右模糊大类。
 * 支持 Au999、Au_18K、au18k、JZJ_au、BR_Ag999 等。
 */
export function resolveMetalBase(metalNum) {
  const raw = String(metalNum || '').trim()
  if (!raw) return ''
  const normalized = normalizeMetalCode(raw)
  if (normalized.length >= 2) {
    const base = normalized.substring(0, 2)
    if (PAID_BASE_METALS.includes(base)) return base
  }
  const lower = raw.toLowerCase()
  for (const base of PAID_BASE_METALS) {
    const b = base.toLowerCase()
    if (lower === b || lower.startsWith(b) || lower.includes(`_${b}`) || lower.endsWith(`_${b}`)) {
      return base
    }
  }
  return ''
}

/** 从价格接口 orderPurity 映射中解析下单纯度（匹配忽略大小写） */
export function resolveOrderPurity(metalCode, orderPurityMap, fallbackPurity) {
  if (!metalCode || !orderPurityMap) {
    return fallbackPurity
  }
  const normalized = normalizeMetalCode(metalCode)
  const keys = [metalCode, normalized, compactMetalCode(metalCode), String(metalCode).toLowerCase()]
  for (const key of keys) {
    if (!key) continue
    const val = orderPurityMap[key] ?? orderPurityMap[String(key).toLowerCase()]
    if (val !== undefined && val !== null && val !== '') {
      const num = Number(val)
      return Number.isNaN(num) ? fallbackPurity : num
    }
  }
  return fallbackPurity
}

/**
 * 盘价 Symbol → 规范金属编码（首字母大写：Au / Au999 / Ag925 …）
 * Symbol 本身保持行情源写法（含 JZJ_au 等）。
 */
export const SYMBOL_TO_METAL_CODE = {
  JZJ_au: 'Au',
  JZJ_ag: 'Ag',
  JZJ_pt: 'Pt',
  JZJ_pd: 'Pd',
  Au999: 'Au999',
  Au_22K: 'Au22K',
  Au_18K: 'Au18K',
  Au_14K: 'Au14K',
  BR_Ag999: 'Ag999',
  BR_Ag925: 'Ag925',
  BR_Pt990: 'Pt990',
  BR_Pt950: 'Pt950',
  BR_Pd990: 'Pd990'
}

/**
 * 规范金属编码 → 盘价 Symbol 别名（与后端 MemberZoneUtils.PRICE_ALIASES 对齐）。
 * key 一律规范大写码；匹配时先 normalize 再查表。
 */
export const METAL_PRICE_ALIASES = {
  Au: ['JZJ_au'],
  Ag: ['JZJ_ag', 'BR_Ag999', 'BR_Ag925'],
  Pt: ['JZJ_pt', 'BR_Pt990', 'BR_Pt950'],
  Pd: ['JZJ_pd', 'BR_Pd990'],
  Au999: ['Au999'],
  Au22K: ['Au_22K'],
  Au18K: ['Au_18K'],
  Au14K: ['Au_14K'],
  Ag999: ['BR_Ag999', 'Ag999'],
  Ag925: ['BR_Ag925', 'Ag925'],
  Pt990: ['BR_Pt990', 'Pt990'],
  Pt950: ['BR_Pt950', 'Pt950'],
  Pd990: ['BR_Pd990', 'Pd990']
}

/** @deprecated 历史会员合并名，仅作兼容；展示已统一用原名 */
export const MEMBER_MERGED_METAL_NAMES = ['黄金9999', '白银9999', '铂金9999', '钯金9999']

/** 写入 metalCode 价格映射（规范码 + 小写别名，兼容历史数据与 OrderPrice 字段） */
export function putMetalCodePrice(map, metalCode, price) {
  if (!map || metalCode == null || metalCode === '' || price == null || Number.isNaN(Number(price))) return
  const code = String(metalCode).trim()
  const num = Number(price)
  const normalized = normalizeMetalCode(code) || code
  const compact = compactMetalCode(code) || normalized
  map[normalized] = num
  map[compact] = num
  map[normalized.toLowerCase()] = num
  map[compact.toLowerCase()] = num
}

/**
 * 构建价格查找 key 列表（规范码 + 紧凑码 + 盘价 Symbol 别名）。
 * 精确优先；未命中时按前两位右模糊回退大类（Au/Ag/Pt/Pd）。
 * @param {string} metalCode
 * @returns {string[]}
 */
export function buildMetalPriceLookupKeys(metalCode) {
  const raw = String(metalCode || '').trim()
  if (!raw) return []
  const normalized = normalizeMetalCode(raw) || raw
  const compact = compactMetalCode(raw) || normalized
  const keys = [raw, normalized, compact]
  const aliases = METAL_PRICE_ALIASES[normalized]
    || METAL_PRICE_ALIASES[compact]
    || METAL_PRICE_ALIASES[normalizeMetalCode(compact)]
  if (aliases) {
    keys.push(...aliases)
  } else {
    keys.push(`BR_${normalized}`, `BR_${compact}`)
  }
  // 前两位右模糊：Au999 → 回退 Au 的盘价别名
  const prefix = metalCodePrefix(raw)
  if (prefix && prefix !== normalized && prefix !== compact) {
    keys.push(prefix)
    const baseAliases = METAL_PRICE_ALIASES[prefix]
    if (baseAliases) keys.push(...baseAliases)
  }
  return keys
}

/**
 * 按金属编码从价格映射解析回购价（匹配忽略大小写）。
 * @param {string} metalCode
 * @param {Record<string, number>} priceMap
 * @returns {number|null}
 */
export function resolveMetalBuyPrice(metalCode, priceMap = {}) {
  if (!metalCode || !priceMap) return null
  for (const key of buildMetalPriceLookupKeys(metalCode)) {
    const val = priceMap[key] ?? priceMap[String(key).toLowerCase()]
    if (val != null && !Number.isNaN(Number(val))) {
      return Number(val)
    }
  }
  return null
}

/**
 * 规范金属编码 → OrderPrice 提交字段名（后端 Java 字段为小写：au / au999 / au22k）。
 * @param {string} metalCode
 * @returns {string}
 */
export function metalCodeToOrderPriceField(metalCode) {
  const compact = compactMetalCode(metalCode)
  return compact ? compact.toLowerCase() : ''
}

/**
 * 取订单定价快照中对应金属的 9999 盘价（字段：au / ag / pt / pd）。
 * 与后端 OrderFeeUtils.resolveMetal9999Price 对齐。
 * @param {object|null|undefined} orderPrice
 * @param {string} metalNum
 * @returns {number|null}
 */
export function resolveMetal9999Price(orderPrice, metalNum) {
  if (!orderPrice) return null
  const base = resolveMetalBase(metalNum)
  let raw = null
  if (base === 'Au') raw = orderPrice.au
  else if (base === 'Ag') raw = orderPrice.ag
  else if (base === 'Pt') raw = orderPrice.pt
  else if (base === 'Pd') raw = orderPrice.pd
  if (raw == null || String(raw).trim() === '') return null
  const n = Number(String(raw).trim())
  return Number.isFinite(n) ? n : null
}

/** 金属大类 → 简称（金/银/铂/钯） */
const METAL_BASE_SHORT_NAME = { Au: '金', Ag: '银', Pt: '铂', Pd: '钯' }

/**
 * 金属大类简称：Au→金、Ag→银、Pt→铂、Pd→钯。
 * @param {string} metalNum
 * @returns {string}
 */
export function resolveMetalBaseShortName(metalNum) {
  return METAL_BASE_SHORT_NAME[resolveMetalBase(metalNum)] || ''
}

/**
 * 单价小数位：白银(Ag) 3 位，其他金属 2 位。
 * 与后端 OrderFeeUtils.formatUnitPrice 对齐。
 * @param {string} metalNum
 * @returns {2|3}
 */
export function getMetalPriceDigits(metalNum) {
  return resolveMetalBase(metalNum) === 'Ag' ? 3 : 2
}

/**
 * 格式化金属单价展示（白银 3 位，其他 2 位）。
 * @param {number|string|null|undefined} price
 * @param {string} metalNum
 * @returns {string}
 */
export function formatMetalUnitPrice(price, metalNum) {
  const n = Number(price)
  if (!Number.isFinite(n)) return ''
  return n.toFixed(getMetalPriceDigits(metalNum))
}

export { formatPurity, formatWeight, formatPrice, formatDecimal } from '@/utils/format.js'
